import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATS_URL = (msg) =>
  `https://api.whatsapp.com/send?phone=554599744439&text=${encodeURIComponent(msg)}`;

const initialForm = {
  full_name: "",
  email: "",
  whatsapp: "",
  company: "",
  role: "",
  sector: "",
  company_size: "",
  challenges: "",
  goals: "",
  consent: false,
};

export default function DiagnosticForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    const V = t.form.validation;
    if (!form.full_name.trim()) err.full_name = V.required;
    if (!form.email.trim()) err.email = V.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = V.email;
    if (!form.whatsapp.trim()) err.whatsapp = V.required;
    if (!form.company.trim()) err.company = V.required;
    if (!form.consent) err.consent = V.consent;
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, language: lang });
      setDone(true);
      setForm(initialForm);
      toast.success(t.form.success_title);
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="diagnostic"
      data-testid="diagnostic-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="sap-container">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left column */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="sap-divider-gold" />
                <span className="sap-overline">{t.form.overline}</span>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h2
                className="font-serif leading-[1.1] tracking-tight"
                style={{
                  fontSize: "clamp(1.9rem, 3.4vw, 3rem)",
                  color: "var(--sap-navy)",
                  fontWeight: 400,
                }}
              >
                {t.form.title}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600">
                {t.form.subtitle}
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-10 p-6 border" style={{ borderColor: "#e7e7e7" }}>
                <div className="sap-overline mb-3">{t.cta_final.overline}</div>
                <p
                  className="font-serif text-xl leading-snug"
                  style={{ color: "var(--sap-navy)", fontWeight: 500 }}
                >
                  {t.cta_final.title}
                </p>
                <a
                  href={WHATS_URL(t.whatsapp_msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="diagnostic-whatsapp-cta"
                  className="mt-6 sap-btn-ghost !py-3"
                >
                  <MessageCircle size={16} strokeWidth={1.75} />
                  WhatsApp
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right column: form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="border p-8 md:p-10" style={{ borderColor: "#e7e7e7" }}>
                {done ? (
                  <div className="text-center py-14" data-testid="diagnostic-success">
                    <CheckCircle2
                      size={48}
                      strokeWidth={1.25}
                      className="mx-auto mb-6"
                      style={{ color: "var(--sap-gold)" }}
                    />
                    <h3
                      className="font-serif text-2xl md:text-3xl"
                      style={{ color: "var(--sap-navy)", fontWeight: 500 }}
                    >
                      {t.form.success_title}
                    </h3>
                    <p className="mt-4 text-neutral-600 max-w-md mx-auto">
                      {t.form.success_body}
                    </p>
                    <button
                      type="button"
                      onClick={() => setDone(false)}
                      className="mt-8 text-sm underline underline-offset-4 tracking-wider uppercase"
                      style={{ color: "var(--sap-navy)" }}
                    >
                      OK
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="diagnostic-form" noValidate>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="sap-label" htmlFor="full_name">
                          {t.form.fields.full_name}
                        </label>
                        <input
                          id="full_name"
                          data-testid="form-full-name"
                          className="sap-input"
                          value={form.full_name}
                          onChange={set("full_name")}
                          autoComplete="name"
                        />
                        {errors.full_name && (
                          <div className="sap-field-error">{errors.full_name}</div>
                        )}
                      </div>
                      <div>
                        <label className="sap-label" htmlFor="email">
                          {t.form.fields.email}
                        </label>
                        <input
                          id="email"
                          type="email"
                          data-testid="form-email"
                          className="sap-input"
                          value={form.email}
                          onChange={set("email")}
                          autoComplete="email"
                        />
                        {errors.email && (
                          <div className="sap-field-error">{errors.email}</div>
                        )}
                      </div>

                      <div>
                        <label className="sap-label" htmlFor="whatsapp">
                          {t.form.fields.whatsapp}
                        </label>
                        <input
                          id="whatsapp"
                          data-testid="form-whatsapp"
                          className="sap-input"
                          value={form.whatsapp}
                          onChange={set("whatsapp")}
                          autoComplete="tel"
                        />
                        {errors.whatsapp && (
                          <div className="sap-field-error">{errors.whatsapp}</div>
                        )}
                      </div>
                      <div>
                        <label className="sap-label" htmlFor="company">
                          {t.form.fields.company}
                        </label>
                        <input
                          id="company"
                          data-testid="form-company"
                          className="sap-input"
                          value={form.company}
                          onChange={set("company")}
                          autoComplete="organization"
                        />
                        {errors.company && (
                          <div className="sap-field-error">{errors.company}</div>
                        )}
                      </div>

                      <div>
                        <label className="sap-label" htmlFor="role">
                          {t.form.fields.role}
                        </label>
                        <input
                          id="role"
                          data-testid="form-role"
                          className="sap-input"
                          value={form.role}
                          onChange={set("role")}
                        />
                      </div>
                      <div>
                        <label className="sap-label" htmlFor="sector">
                          {t.form.fields.sector}
                        </label>
                        <input
                          id="sector"
                          data-testid="form-sector"
                          className="sap-input"
                          value={form.sector}
                          onChange={set("sector")}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="sap-label">{t.form.fields.size}</label>
                        <Select
                          value={form.company_size}
                          onValueChange={(v) => set("company_size")(v)}
                        >
                          <SelectTrigger
                            data-testid="form-size"
                            className="sap-input !h-auto !py-3 justify-between rounded-none"
                          >
                            <SelectValue placeholder={t.form.fields.size_placeholder} />
                          </SelectTrigger>
                          <SelectContent>
                            {t.form.fields.size_options.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="sap-label" htmlFor="challenges">
                          {t.form.fields.challenges}
                        </label>
                        <textarea
                          id="challenges"
                          data-testid="form-challenges"
                          rows={3}
                          className="sap-textarea"
                          placeholder={t.form.fields.challenges_ph}
                          value={form.challenges}
                          onChange={set("challenges")}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="sap-label" htmlFor="goals">
                          {t.form.fields.goals}
                        </label>
                        <textarea
                          id="goals"
                          data-testid="form-goals"
                          rows={3}
                          className="sap-textarea"
                          placeholder={t.form.fields.goals_ph}
                          value={form.goals}
                          onChange={set("goals")}
                        />
                      </div>

                      <div className="md:col-span-2 flex items-start gap-3 mt-2">
                        <Checkbox
                          id="consent"
                          data-testid="form-consent"
                          checked={form.consent}
                          onCheckedChange={(v) => set("consent")(!!v)}
                          className="mt-1 rounded-none border-neutral-400 data-[state=checked]:bg-[color:var(--sap-navy)] data-[state=checked]:border-[color:var(--sap-navy)]"
                        />
                        <label
                          htmlFor="consent"
                          className="text-xs leading-relaxed text-neutral-600 cursor-pointer"
                        >
                          {t.form.fields.consent}
                        </label>
                      </div>
                      {errors.consent && (
                        <div className="md:col-span-2 sap-field-error">
                          {errors.consent}
                        </div>
                      )}

                      <div className="md:col-span-2 mt-2">
                        <button
                          type="submit"
                          data-testid="form-submit"
                          disabled={loading}
                          className="sap-btn-primary w-full md:w-auto"
                        >
                          {loading ? t.form.fields.sending : t.form.fields.submit}
                          {!loading && <ArrowRight size={16} strokeWidth={1.75} />}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
